import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web/common/localization/localize";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import {
  EyebrowText,
  Section,
  SectionHeadline,
  LimitedSectionContent,
  DefinitionTerm,
  DefinitionDescription,
} from "@faire/retailer/views/careers/__internal__/common/common";
import {
  StyledCTAButton,
  SignUpButtonContent,
  StyledArrowRight,
} from "@faire/retailer/views/careers/ViewOpeningsButton";

import { FAIRE_VALUES, IValueProps } from "./faireValues";

const OurValuesSectionBase = () => {
  const { isTablet, isXLargeDesktop } = useViewport();
  const { strictLocalize } = useStrictLocalization();
  return (
    <Section
      id="values"
      align="center"
      $withHorizontalPadding
      $withVerticalPadding
      $backgroundColor={Core.surface.inverse}
    >
      <LimitedSectionContent>
        <EyebrowText>
          <StrictLocalMsg
            defaultMessage="Our Values"
            description={{
              text: "Title to a section on the company's core values",
            }}
          />
        </EyebrowText>
      </LimitedSectionContent>
      <ResponsiveContent>
        <HeadlineContainer>
          <Spacer size="3x" />
          <SectionHeadline>
            <StrictLocalMsg
              defaultMessage="Faire is being built for entrepreneurs, by entrepreneurs."
              description={{
                text: "Heading of a section describing the company Faire",
                img: "https://cdn.faire.com/fastly/f0f3fdb250adfb8573161d765111cb02adce55137995bc70db98fbdab6ac758e.png",
              }}
            />
          </SectionHeadline>
          <Spacer size="3x" />
          <Typography variant="subheadingSansMedium">
            <StrictLocalMsg
              defaultMessage="Every day is an adventure at Faire. Our core values guide the work we do and the culture we’re building."
              description={{
                text: "description of a section describing the company Faire",
              }}
            />
          </Typography>
          <Spacer height="5x" />
          <StyledCTAButton linkTo="https://news.faire.com/2021/11/04/what-defines-faire/">
            <SignUpButtonContent>
              <StrictLocalMsg
                defaultMessage="What Defines Faire"
                description={{
                  text: "Button text redirects to what defines faire page",
                }}
              />
              <StyledArrowRight />
            </SignUpButtonContent>
          </StyledCTAButton>
        </HeadlineContainer>
        <Column align={isTablet ? "flex-end" : "flex-start"}>
          <Spacer height="5x" />
          {FAIRE_VALUES(strictLocalize).map((faireValue, index) => (
            <React.Fragment key={faireValue.title}>
              {index !== 0 ? (
                <Spacer size={isXLargeDesktop ? "5x" : "4x"} />
              ) : null}
              <Value {...faireValue} />
            </React.Fragment>
          ))}
        </Column>
      </ResponsiveContent>
    </Section>
  );
};

export const OurValuesSection = observer(OurValuesSectionBase);

const ResponsiveContent = styled(LimitedSectionContent).attrs({
  justify: "space-between",
})`
  ${responsiveMediaQueries.desktopAndAbove} {
    flex-direction: row;
  }
`;

const Value = ({ id, title, description }: IValueProps) => {
  return (
    <ValueDefinitionList id={id}>
      <DefinitionTerm>{title}</DefinitionTerm>
      <Spacer size="1x" />
      <DefinitionDescription>{description}</DefinitionDescription>
    </ValueDefinitionList>
  );
};

const ValueDefinitionList = styled.dl`
  max-width: 428px;

  ${responsiveMediaQueries.xLargeAndAbove} {
    max-width: 496px;
  }
`;

const HeadlineContainer = styled(Column)`
  ${responsiveMediaQueries.tabletAndBelow} {
    max-width: 252px;
  }
  ${responsiveMediaQueries.desktopAndAbove} {
    max-width: 550px;
  }
`;
